import { LocalStorage } from 'src/utils/LocalStorage'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { UseFormReturn } from 'react-hook-form/dist/types/form'
import userTreatmentRequest from 'src/api/requests/userTreatment'
import { FormValues } from 'src/containers/MedicalSpecialty/components/useMedicalSpecialtyForm'
import userTreatment from 'src/store/domain/myPage/userTreatment'
import detailUserTreatment from 'src/store/domain/myPage/detailUserTreatment'
import setNotificationActions from 'src/store/ui/notification'

export interface PostCreateUserTreatment {
  data: FormValues
  form: UseFormReturn<FormValues, any>
}

export interface UpdatePlanSelectionPayload {
  id: string | number
  has_selected_plan?: boolean
  plan_id?: string | number | null
}

export type PayloadIndexUserTreatmentType = {
  with?: string
}

export const getUserTreatmentList = createAsyncThunk<
  any,
  PayloadIndexUserTreatmentType
>('get_user_treatment_list', async (params, { dispatch }) => {
  try {
    dispatch(userTreatment.updateLoading(true))
    dispatch(userTreatment.updateEntities([]))
    const res = await userTreatmentRequest.index(params)

    dispatch(userTreatment.updateEntities(res.data.data))
    dispatch(userTreatment.updateLoading(false))
  } catch (error) {
    dispatch(userTreatment.updateLoading(false))
  }
})

export const createUserTreatment = createAsyncThunk<
  any,
  PostCreateUserTreatment
>('create_user_treatment', async ({ data, form }, { dispatch }) => {
  try {
    const response = await userTreatmentRequest.create(data)
    const userTreatment = response.data.data
    LocalStorage.setUserTreatmentId(String(userTreatment.id))
    LocalStorage.setMenuName(String(userTreatment.menu.name))

    return userTreatment
  } catch (err: any) {
    form.setError('menu_id', {
      type: 'manual',
      message: '入力してください。',
    })
    dispatch(
      setNotificationActions.onNotification({
        type: 'error',
        message: err?.response?.data?.message,
      }),
    )
    throw err
  }
})

export const getDetailUserTreatment = createAsyncThunk<any, any>(
  'get_user_treatment_detail',
  async ({ id, params }, { dispatch }) => {
    try {
      dispatch(detailUserTreatment.updateLoading(true))
      const res = await userTreatmentRequest.show(id, params)

      dispatch(detailUserTreatment.updateEntity(res.data.data))
      dispatch(detailUserTreatment.updateLoading(false))
    } catch (error) {
      dispatch(detailUserTreatment.updateLoading(false))
    }
  },
)

// Fetches a userTreatment's plan without touching the shared
// `detailUserTreatment` slice, which is also populated with fuller payloads by
// MyPage/Treatment, AutomaticPlan/SuggestSubscription and Video/Interview —
// writing a partial `with: 'plan'` response there would risk those screens
// reading stale/incomplete data (Task No.143 review feedback).
export const getUserTreatmentPlan = createAsyncThunk<any, any>(
  'get_user_treatment_plan',
  async ({ id, params }) => {
    const res = await userTreatmentRequest.show(id, params)

    return res.data?.data?.plan ?? null
  },
)

export const updateUserTreatment = createAsyncThunk<any, any>(
  'update_user_treatmen',
  async ({ id, data }, { dispatch }) => {
    try {
      dispatch(detailUserTreatment.updateLoading(true))
      const res = await userTreatmentRequest.update(id, data)

      dispatch(detailUserTreatment.updateEntity(res.data.data))
      dispatch(detailUserTreatment.updateLoading(false))

      return res
    } catch (error: any) {
      dispatch(detailUserTreatment.updateLoading(false))

      dispatch(
        setNotificationActions.onNotification({
          type: 'error',
          message: error?.response?.data?.message || '更新に失敗しました。',
        }),
      )
      throw error
    }
  },
)

export const updateCallState = createAsyncThunk<any, any>(
  'update-call-state',
  async ({ id, state }, { dispatch }) => {
    try {
      dispatch(detailUserTreatment.updateLoading(true))
      const res = await userTreatmentRequest.updateCallState(id, state)

      dispatch(detailUserTreatment.updateEntity(res.data.data))
      dispatch(detailUserTreatment.updateLoading(false))
    } catch (error) {
      dispatch(detailUserTreatment.updateLoading(false))
    }
  },
)

export const updatePlanSelection = createAsyncThunk<
  any,
  UpdatePlanSelectionPayload
>('update_plan_selection', async ({ id, ...data }, { dispatch }) => {
  try {
    dispatch(detailUserTreatment.updateLoading(true))
    const res = await userTreatmentRequest.updatePlanSelection(id, data)

    dispatch(detailUserTreatment.updateEntity(res.data.data))
    dispatch(detailUserTreatment.updateLoading(false))
    return res.data.data
  } catch (error: any) {
    dispatch(detailUserTreatment.updateLoading(false))
    dispatch(
      setNotificationActions.onNotification({
        type: 'error',
        message: error?.response?.data?.message || '更新に失敗しました。',
      }),
    )
    throw error
  }
})

export const fetcherUserTreatmentFromMail = async (id, params) => {
  const userTreatment = (await userTreatmentRequest.show(id, params)).data.data

  LocalStorage.setUserTreatmentId(userTreatment.id)
  LocalStorage.setMenuName(userTreatment.menu?.name)

  return userTreatment
}

export const cancelUserTreatment = async (
  id: number | string,
  payload: { reason_code: string; reason_text?: string | null },
) => {
  const res = await userTreatmentRequest.requestCancel(id, payload)

  return res
}

export const getCancelModalTreatments = async (params: {
  with: string
  not_state?: string
  state?: string
}) => {
  const res = await userTreatmentRequest.index(params)
  return res.data.data as any[]
}

export const getReservationUserTreatment = createAsyncThunk<any, any>(
  'get_user_treatment_detail',
  async ({ id, params }, { dispatch }) => {
    try {
      dispatch(detailUserTreatment.updateLoading(true))
      const res = await userTreatmentRequest.show(id, params)

      dispatch(detailUserTreatment.updateEntity(res.data.data))
      dispatch(detailUserTreatment.updateLoading(false))
    } catch (error) {
      dispatch(detailUserTreatment.updateLoading(false))
    }
  },
)

export const getInProgressUserTreatments = createAsyncThunk<any, void>(
  'get_in_progress_user_treatments',
  async (_, { dispatch }) => {
    try {
      const res = await userTreatmentRequest.index({
        state: 'in_progress',
        with: 'menu',
      })

      return res.data.data
    } catch (err: any) {
      dispatch(
        setNotificationActions.onNotification({
          type: 'error',
          message: err?.response?.data?.message,
        }),
      )
      throw err
    }
  },
)

export const continueUserTreatment = createAsyncThunk<
  any,
  { id: string | number }
>('continue_user_treatment', async ({ id }, { dispatch }) => {
  try {
    const response = await userTreatmentRequest.continueTreatment(id)
    const userTreatment = response.data.data
    LocalStorage.setUserTreatmentId(String(userTreatment.id))
    LocalStorage.setMenuName(userTreatment.menu?.name ?? '')

    return userTreatment
  } catch (err: any) {
    dispatch(
      setNotificationActions.onNotification({
        type: 'error',
        message: err?.response?.data?.message,
      }),
    )
    throw err
  }
})

// No.201: BE returns this dedicated error code (instead of the generic
// user_treatment_already_exists) when the menu was already judged
// prescription-NG, so the caller can redirect to the NG screen instead of
// showing a generic toast.
export const USER_TREATMENT_PRESCRIPTION_NG_CODE =
  'errors.user_treatment_prescription_ng'

export const checkExistsUserTreatment = createAsyncThunk<
  any,
  { menu_id: string | number }
>(
  'check_exists_user_treatment',
  async ({ menu_id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await userTreatmentRequest.checkExists({ menu_id })
      const existsTreatment = response.data.data

      return existsTreatment
    } catch (err: any) {
      // [SCOPE] No.201: suppress the generic toast only for the
      // prescription-NG code so useMedicalSpecialtyForm can redirect to the
      // dedicated NG screen instead. Every other rejection keeps showing the
      // toast exactly as before.
      if (err?.response?.data?.code !== USER_TREATMENT_PRESCRIPTION_NG_CODE) {
        dispatch(
          setNotificationActions.onNotification({
            type: 'error',
            message: err?.response?.data?.message,
          }),
        )
      }
      // rejectWithValue (instead of throw) preserves the original response
      // body — including `code` — on action.payload for callers that need to
      // branch on it, while existing `.rejected.match(action)` checks in
      // other consumers (e.g. CheckUserExistsModal) keep working unchanged.
      return rejectWithValue(err?.response?.data)
    }
  },
)
